import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Link } from "react-router-dom";
import {
  adminFetchBtwMailContext,
  adminSendBtwCustomerEmail,
  adminFetchBtwEmailTemplates,
} from "../core/axios/endpoints";
import { useBodyScrollLock } from "../core/scroll-lock";
import RichTextEditor from "./rich-text-editor";

// Templates uit DB zijn plain text met \n line-breaks (legacy). Voor TipTap
// converteren we ze naar HTML met <p>-tags per paragraaf. Als de template al
// HTML-tags bevat, laten we 'm ongemoeid.
const escapeHtml = (s) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const toEditorHtml = (text) => {
  if (!text) return "";
  if (/<\/?[a-z][^>]*>/i.test(text)) return text;
  return text
    .split(/\n\s*\n/)
    .map((p) => escapeHtml(p).replace(/\n/g, "<br />"))
    .map((p) => `<p>${p}</p>`)
    .join("");
};

// Zelfde regex als de server (utils/btwMailPlaceholders.ts). Alleen voor
// het voorbeeld en de waarschuwingen; de server vult de echte mail in.
const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g;
const findPlaceholders = (text) => {
  const keys = new Set();
  let m;
  PLACEHOLDER_RE.lastIndex = 0;
  while ((m = PLACEHOLDER_RE.exec(text ?? "")) !== null) keys.add(m[1]);
  return Array.from(keys);
};
const escapeHtmlValue = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
const fillPlaceholders = (text, values, html) =>
  (text ?? "").replace(PLACEHOLDER_RE, (match, key) => {
    if (!(key in values)) return match;
    return html ? escapeHtmlValue(values[key]) : values[key];
  });

const GROEP_LABELS = {
  klant: "Klant",
  aanvraag: "Aanvraag",
  berekening: "Berekening",
};

const SendCustomerEmailModal = ({
  isOpen,
  applicationId,
  customerName,
  customerEmail,
  onClose,
  onSent,
}) => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [toEmail, setToEmail] = useState(customerEmail ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [mailCtx, setMailCtx] = useState(null);
  const [showVars, setShowVars] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const editorRef = useRef(null);
  const onEditorReady = useCallback((ed) => {
    editorRef.current = ed;
  }, []);

  useEffect(() => {
    if (!isOpen || !applicationId) return undefined;
    let cancelled = false;
    adminFetchBtwMailContext(applicationId)
      .then((res) => {
        if (!cancelled) setMailCtx(res?.data ?? res);
      })
      .catch(() => {
        if (!cancelled) setMailCtx(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, applicationId]);

  const insertVariable = (key) => {
    const ed = editorRef.current;
    const token = `{{${key}}}`;
    if (ed && !ed.isDestroyed) {
      ed.chain().focus().insertContent(token).run();
    } else {
      setBody((b) => `${b}${token}`);
    }
  };

  const placeholders = mailCtx?.placeholders ?? {};
  const calc = mailCtx?.calc;
  const usedKeys = useMemo(
    () => Array.from(new Set([...findPlaceholders(subject), ...findPlaceholders(body)])),
    [subject, body],
  );
  const knownKeys = useMemo(
    () => new Set((mailCtx?.definities ?? []).map((d) => d.key)),
    [mailCtx],
  );
  const berekeningKeys = useMemo(
    () =>
      new Set(
        (mailCtx?.definities ?? [])
          .filter((d) => d.groep === "berekening")
          .map((d) => d.key),
      ),
    [mailCtx],
  );
  const unknownKeys = usedKeys.filter((k) => mailCtx && !knownKeys.has(k));
  // Optionele variabelen (tussenvoegsel, partnerNaam) mogen leeg zijn; de
  // server blokkeert daar ook niet op.
  const optionalKeys = useMemo(
    () =>
      new Set(
        (mailCtx?.definities ?? []).filter((d) => d.optioneel).map((d) => d.key),
      ),
    [mailCtx],
  );
  const emptyKeys = usedKeys.filter(
    (k) => knownKeys.has(k) && !placeholders[k] && !optionalKeys.has(k),
  );
  const usesBerekening = usedKeys.some((k) => berekeningKeys.has(k));
  const berekeningBlokkeert = usesBerekening && calc && !calc.compleet;
  const previewSubject = fillPlaceholders(subject, placeholders, false);
  const previewBody = fillPlaceholders(body, placeholders, true);
  const varProblem =
    unknownKeys.length > 0 || emptyKeys.length > 0 || berekeningBlokkeert;

  useBodyScrollLock(isOpen);

  // Templates ophalen bij open. We cachen niet bewust - admin kan ze net
  // bewerken hebben en we willen direct de verse versie.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setTemplatesLoading(true);
    adminFetchBtwEmailTemplates()
      .then((res) => {
        if (cancelled) return;
        setTemplates(res?.data ?? res ?? []);
      })
      .catch((e) => {
        if (cancelled) return;
        const msg =
          e?.response?.data?.error ?? e?.message ?? "Templates laden mislukt.";
        setError(msg);
        setTemplates([]);
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSubject("");
      setBody("");
      setError(null);
    } else {
      // Bij open reset het naar-adres naar het adres uit de aanvraag, tenzij
      // admin het expliciet in deze sessie heeft aangepast.
      setToEmail(customerEmail ?? "");
    }
  }, [isOpen, customerEmail]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, submitting, onClose]);

  const applyTemplate = (tpl) => {
    setSubject(tpl.subject ?? "");
    setBody(toEditorHtml(tpl.body ?? ""));
  };

  // TipTap output bevat HTML-tags. Voor de minimum-lengte-check willen we
  // alleen de zichtbare tekst tellen.
  const bodyTextLength = body
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim().length;

  const submit = async () => {
    if (subject.trim().length < 3 || bodyTextLength < 10) {
      setError("Onderwerp + bericht moeten gevuld zijn.");
      return;
    }
    const trimmedTo = toEmail.trim();
    if (!trimmedTo || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedTo)) {
      setError("Geef een geldig e-mailadres op om naar te sturen.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await adminSendBtwCustomerEmail(applicationId, {
        subject: subject.trim(),
        body: body.trim(),
        toEmail: trimmedTo,
      });
      onSent?.({ subject: subject.trim(), toEmail: trimmedTo });
      onClose();
    } catch (e) {
      setError(
        e?.response?.data?.error ?? e?.message ?? "Mail versturen mislukt.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[10000] flex items-start justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-sm">
      <div className="my-8 w-full max-w-2xl rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 p-6">
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-xl text-gray-900">
              Mail naar klant
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Stuur een persoonlijk bericht aan <strong>{customerName}</strong>.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100"
            aria-label="Sluiten"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.3 4.3a1 1 0 011.4 0L10 8.6l4.3-4.3a1 1 0 111.4 1.4L11.4 10l4.3 4.3a1 1 0 11-1.4 1.4L10 11.4l-4.3 4.3a1 1 0 11-1.4-1.4L8.6 10 4.3 5.7a1 1 0 010-1.4z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {/* Naar-adres (aanpasbaar) */}
          <label className="mb-5 block text-sm">
            <span className="font-medium text-gray-900">Naar</span>
            <div className="relative mt-1">
              <input
                type="email"
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                disabled={submitting}
                placeholder="naam@voorbeeld.nl"
                className="w-full rounded-md border border-gray-300 px-3 py-2 pr-20 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
              {customerEmail && toEmail !== customerEmail ? (
                <button
                  type="button"
                  onClick={() => setToEmail(customerEmail)}
                  disabled={submitting}
                  className="absolute inset-y-0 right-2 my-auto h-7 rounded-md px-2 text-xs font-medium text-gray-600 hover:bg-gray-100"
                >
                  reset
                </button>
              ) : null}
            </div>
            {customerEmail && toEmail && toEmail !== customerEmail ? (
              <p className="mt-1 text-[11px] text-amber-700">
                Let op: dit is een ander adres dan dat van de aanvraag ({customerEmail}).
              </p>
            ) : null}
          </label>

          {/* Templates uit DB */}
          <div className="mb-5">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                Snel beginnen met
              </p>
              <Link
                to="/panel/admin/btw-email-templates"
                onClick={onClose}
                className="text-xs text-gray-500 underline hover:text-gray-900"
              >
                Templates beheren &rarr;
              </Link>
            </div>
            {templatesLoading ? (
              <p className="text-xs text-gray-400">Templates laden…</p>
            ) : templates.length === 0 ? (
              <p className="text-xs text-gray-400">
                Geen templates. Maak ze aan via &quot;Templates beheren&quot;.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {templates.map((tpl) => (
                  <button
                    key={tpl._id}
                    type="button"
                    onClick={() => applyTemplate(tpl)}
                    disabled={submitting}
                    className="rounded-full border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:border-emerald-500 hover:bg-emerald-50 disabled:opacity-50"
                  >
                    {tpl.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <label className="mb-4 block text-sm">
            <span className="font-medium text-gray-900">Onderwerp</span>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Onderwerp van de mail"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              disabled={submitting}
            />
          </label>

          <div className="block text-sm">
            <span className="font-medium text-gray-900">Bericht</span>
            <div className="mt-1">
              <RichTextEditor
                value={body}
                onChange={setBody}
                onReady={onEditorReady}
                disabled={submitting}
                placeholder="Schrijf hier de boodschap aan de klant. Gebruik vet, lijsten of links waar nuttig."
              />
            </div>
          </div>

          {/* Variabelen: de server vult {{...}} in bij verzenden. */}
          <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => setShowVars((v) => !v)}
                className="font-semibold text-gray-700 hover:text-gray-900"
              >
                {showVars ? "▾" : "▸"} Variabelen invoegen
              </button>
              <button
                type="button"
                onClick={() => setShowPreview((v) => !v)}
                disabled={!mailCtx}
                className="rounded-md border border-gray-300 bg-white px-2 py-1 font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              >
                {showPreview ? "Voorbeeld verbergen" : "Voorbeeld met echte waarden"}
              </button>
            </div>
            {showVars ? (
              <div className="mt-2 space-y-2">
                {["klant", "aanvraag", "berekening"].map((groep) => (
                  <div key={groep}>
                    <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                      {GROEP_LABELS[groep]}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {(mailCtx?.definities ?? [])
                        .filter((d) => d.groep === groep)
                        .map((d) => {
                          const val = placeholders[d.key];
                          return (
                            <button
                              key={d.key}
                              type="button"
                              onClick={() => insertVariable(d.key)}
                              disabled={submitting}
                              title={`${d.label}${
                                val
                                  ? `: ${val}`
                                  : d.optioneel
                                    ? " (leeg voor deze aanvraag, valt weg)"
                                    : " (geen waarde voor deze aanvraag)"
                              }`}
                              className={`rounded-full border px-2 py-0.5 font-mono text-[11px] ${
                                val || d.optioneel
                                  ? "border-gray-300 bg-white text-gray-700 hover:border-emerald-500 hover:bg-emerald-50"
                                  : "border-dashed border-amber-300 bg-amber-50 text-amber-800"
                              }`}
                            >
                              {`{{${d.key}}}`}
                            </button>
                          );
                        })}
                    </div>
                  </div>
                ))}
                {calc?.compleet ? (
                  <p className="text-[11px] text-gray-500">
                    Berekening: BTW op factuur {placeholders.btwFactuur}, afdracht
                    eerste jaar {placeholders.afdrachtEersteJaar} ({placeholders.afdrachtPerJaar} per
                    jaar x {calc.eersteJaar?.dagenActief}/{calc.eersteJaar?.dagenInJaar} dagen vanaf
                    {" "}{placeholders.startdatumAfdracht || placeholders.installatiedatum}), netto {placeholders.nettoTeruggave}.
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>

          {berekeningBlokkeert ? (
            <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
              <strong>Bedragen ontbreken.</strong> Deze mail gebruikt de berekening,
              maar die kan niet gemaakt worden: {calc.warnings.join(" ")} Vul eerst
              het aankoopbedrag en de capaciteit aan bij &quot;Gegevens voor
              BTW-teruggave&quot;.
            </div>
          ) : null}
          {unknownKeys.length > 0 ? (
            <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
              <strong>Onbekende variabele(n):</strong>{" "}
              {unknownKeys.map((k) => `{{${k}}}`).join(", ")}. Kies een variabele uit
              de lijst hierboven.
            </div>
          ) : null}
          {emptyKeys.length > 0 && !berekeningBlokkeert ? (
            <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
              <strong>Geen waarde voor deze aanvraag:</strong>{" "}
              {emptyKeys.map((k) => `{{${k}}}`).join(", ")}. Haal ze uit de mail of vul
              de gegevens aan.
            </div>
          ) : null}

          {showPreview && mailCtx ? (
            <div className="mt-3 rounded-md border border-gray-200 bg-white p-3">
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                Voorbeeld zoals de klant het ontvangt
              </p>
              <p className="text-sm font-semibold text-gray-900">{previewSubject || "(geen onderwerp)"}</p>
              <p className="mt-2 text-sm text-gray-500">Beste {placeholders.aanhef},</p>
              <div
                // Zelfde opmaakregels als het style-blok in de mail-shell
                // (templates/partials/header.html), zodat het voorbeeld klopt
                // met wat de klant ontvangt.
                className="prose-sm mt-2 max-w-none text-sm text-gray-900 [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_h3]:mt-4 [&_h3]:mb-1 [&_h3]:text-[15px] [&_h3]:font-bold [&_h3]:text-gray-900 [&_h3:first-child]:mt-0 [&_strong]:font-bold [&_strong]:text-gray-900 [&_a]:text-emerald-700 [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-emerald-400 [&_blockquote]:bg-emerald-50/60 [&_blockquote]:py-2 [&_blockquote]:pl-3"
                // Body is de eigen editor-output (TipTap), de ingevulde waarden
                // zijn ge-escaped. De server sanitized bij verzenden alsnog.
                dangerouslySetInnerHTML={{ __html: previewBody }}
              />
            </div>
          ) : null}

          <div className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-900 ring-1 ring-emerald-200">
            <strong>Info:</strong> de mail wordt verstuurd vanaf{" "}
            <span className="font-mono">noreply@email.subsidie2gether.nl</span>{" "}
            met reply-to naar <span className="font-mono">info@subsidie2gether.nl</span>.
            Klant beantwoordt automatisch naar info-inbox.
          </div>

          {error ? (
            <div className="mt-3 rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900">
              {error}
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 p-4">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={
              submitting ||
              subject.trim().length < 3 ||
              bodyTextLength < 10 ||
              varProblem
            }
            className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
          >
            {submitting ? "Verzenden..." : "Verstuur mail"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SendCustomerEmailModal;
